import { toRequestSpec } from './filterSpec'

/**
 * The old api.js talked HTTP to the Express server; this one talks to the engine
 * worker. Same function names, same request and response shapes, so the hooks
 * and panels run unchanged. Files go to the worker as File objects — the browser
 * reads them in place, and nothing ever leaves the machine.
 */

const worker = new Worker(new URL('./engine/worker.js', import.meta.url), { type: 'module' })

let seq = 0
const pending = new Map()

worker.onmessage = (e) => {
  const { id, ok, result, error } = e.data
  const p = pending.get(id)
  if (!p) return
  pending.delete(id)
  if (ok) p.resolve(result)
  else p.reject(new Error(error))
}

worker.onerror = (e) => {
  // A worker crash strands every in-flight request; failing them beats hanging.
  const err = new Error(e.message || 'The engine crashed — reload the page.')
  for (const p of pending.values()) p.reject(err)
  pending.clear()
}

const call = (op, args) =>
  new Promise((resolve, reject) => {
    const id = ++seq
    pending.set(id, { resolve, reject })
    worker.postMessage({ id, op, args })
  })

export const openFile = (file, forId = null) => call('open', { file, forId })
export const getStatus = (id) => call('status', { id })
export const query = (body) => call('query', body)
export const duplicates = (body) => call('duplicates', body)
export const insights = (body) => call('insights', body)
export const compare = (body) => call('compare', body)
export const emailCheck = (body) => call('emailCheck', body)
export const closeFile = (id) => call('close', { id })

/**
 * Open a file the user picked for comparison. The name survives from the server
 * days, but nothing is uploaded — the file is opened in place like any other,
 * and `forId` gets back a suggested column mapping against that dataset.
 */
export const uploadFile = (file, forId) => openFile(file, forId)

/**
 * Build one export in the worker and hand it to the browser as a download.
 *
 * @param batch `{size, part, parts, offset?}` — rows per file, which file
 *   (1-based), how many there are, and — when the batches are not all the same
 *   size — which matching row (0-based) this one starts at. Null: one file.
 * @param extra optional added columns, `[{name, value}]`, written onto every row
 * @param shape comparison exports only: 'rows' or 'cells'
 */
export async function downloadExport({ id, filters, dup, cmp, columns, batch, extra, shape }) {
  const params = {
    id,
    filters: toRequestSpec(filters),
    columns: columns?.length ? columns : null,
    extra: extra?.length ? extra : null,
    batch: batch ?? null,
  }
  if (cmp) {
    params.kind = 'compare'
    params.cmp = { other: cmp.other, mode: cmp.mode, shape: shape ?? 'rows', spec: cmp.spec }
    params.dup = dup ?? null
  } else if (dup) {
    params.kind = 'duplicates'
    params.dup = dup
  } else {
    params.kind = 'filtered'
  }
  const { blob, filename } = await call('export', params)

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Give the download a minute to start before the URL is released.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

// Saved views live in the browser's own storage — per user by construction.
export { listViews, saveView, updateView, deleteView } from './views'
